import type { Sensitivity } from "../settings/types";
import type { ValueOf, ValueTypeName } from "../settings/value-types";

export type RuleId = `${string}_RULE`;

/**
 * What a rule is (WALLIYO-0066).
 *
 * A rule is one value for the whole company, e.g. the refund amount that needs
 * the Owner's approval, or how long ID images are kept. Unlike a setting, it has
 * no scope: it is never set for one person or role.
 *
 * Rules can carry legal weight, so a value is never overwritten. Each change is
 * a new version with an effective date, a reason, and optionally its legal
 * basis, which means we can always show what the rule was on a given day.
 *
 * As with settings, developers define rules in code; staff with the
 * `editRules` feature change their values.
 */

/** Groups rules on the console's rules screen. */
export type RuleCategory = "payments" | "support" | "safety" | "onboarding" | "jobs" | "access";

export interface Rule<I extends RuleId = RuleId, N extends ValueTypeName = ValueTypeName> {
  /**
   * Permanent, readable identity: <NAME>_RULE, e.g.
   * REFUND_OWNER_APPROVAL_ABOVE_RULE. Code, versions and the audit log all
   * use it. Never changes.
   */
  ruleId: I;
  /** What it is called, in English: the source text Règles shows (#185). */
  name: string;
  category: RuleCategory;
  /** The named value type, from VALUE_TYPES; the same types settings use. */
  valueType: N;
  defaultValue: ValueOf<N>;
  /** Who may change it: `critical` needs the Owner. */
  sensitivity: Sensitivity;
  /** A change must cite a law, contract or legal advice, not only a reason. */
  requiresLegalBasis: boolean;
  /** Retired rules are kept, never deleted, because past versions and the audit log refer to them. */
  status: "active" | "retired";
}

/** One version of a rule's value. Versions are only ever added. */
export interface RuleVersion<N extends ValueTypeName = ValueTypeName> {
  ruleId: RuleId;
  value: ValueOf<N>;
  effectiveFrom: string;
  /** Always required: every rule change says why. */
  reason: string;
  legalBasis: string | null;
  setByStaffMemberId: string;
}

export function defineRule<const I extends RuleId, const N extends ValueTypeName>(
  rule: Omit<Rule<I, N>, "status"> & { status?: "active" | "retired" },
): Rule<I, N> {
  return { ...rule, status: rule.status ?? "active" };
}
